/**
 * NetworkManager - управление состоянием сети и периодическая проверка подключения
 */
import { LogsRepo } from '../offline/logsRepo'

export type NetworkStatus = 'online' | 'offline' | 'checking'

interface NetworkManagerState {
  status: NetworkStatus
  lastOnlineAt: Date | null
  lastOfflineAt: Date | null
  checkInterval: number | null
  listeners: Set<(status: NetworkStatus) => void>
}

export class NetworkManager {
  private static state: NetworkManagerState = {
    status: navigator.onLine ? 'online' : 'offline',
    lastOnlineAt: navigator.onLine ? new Date() : null,
    lastOfflineAt: navigator.onLine ? null : new Date(),
    checkInterval: null,
    listeners: new Set(),
  }

  private static readonly PING_INTERVAL_ONLINE = 60000 // 60 секунд при онлайне
  private static readonly PING_INTERVAL_OFFLINE = 15000 // 15 секунд при офлайне (faster reconnect)
  private static readonly PING_TIMEOUT = 5000 // 5 секунд таймаут для ping
  private static readonly API_HEALTH_ENDPOINT = '/health' // Относительный путь
  private static readonly API_FALLBACK_ENDPOINT = '/api/v1' // Fallback endpoint

  /**
   * Инициализирует NetworkManager и запускает мониторинг
   */
  static init(): () => void {
    console.log('[NetworkManager] Initializing...')
    
    // Слушаем нативные события браузера
    const handleOnline = () => this.handleBrowserOnline()
    const handleOffline = () => this.handleBrowserOffline()
    
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Запускаем периодическую проверку
    this.startPeriodicCheck()

    // Начальная проверка
    this.checkConnection()

    // Возвращаем функцию для очистки
    return () => {
      console.log('[NetworkManager] Cleaning up...')
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      this.stopPeriodicCheck()
    }
  }

  /**
   * Подписывается на изменения статуса сети
   */
  static subscribe(listener: (status: NetworkStatus) => void): () => void {
    this.state.listeners.add(listener)
    
    // Сразу вызываем listener с текущим статусом
    listener(this.state.status)
    
    // Возвращаем функцию для отписки
    return () => {
      this.state.listeners.delete(listener)
    }
  }

  /**
   * Получает текущий статус сети
   */
  static getStatus(): NetworkStatus {
    return this.state.status
  }

  /**
   * Проверяет, онлайн ли сейчас
   */
  static isOnline(): boolean {
    return this.state.status === 'online'
  }

  /**
   * Получает время последнего онлайна
   */
  static getLastOnlineAt(): Date | null {
    return this.state.lastOnlineAt
  }

  /**
   * Получает время последнего офлайна
   */
  static getLastOfflineAt(): Date | null {
    return this.state.lastOfflineAt
  }

  /**
   * Обработчик нативного события 'online'
   */
  private static handleBrowserOnline(): void {
    console.log('[NetworkManager] Browser detected online')
    
    // Проверяем реальное подключение к API
    this.checkConnection()
  }

  /**
   * Обработчик нативного события 'offline'
   */
  private static handleBrowserOffline(): void {
    console.log('[NetworkManager] Browser detected offline')
    this.setStatus('offline')
  }

  /**
   * Проверяет подключение к API с fallback
   */
  private static async checkConnection(): Promise<void> {
    // Не проверяем, если браузер говорит что офлайн
    if (!navigator.onLine) {
      this.setStatus('offline')
      return
    }

    const prevStatus = this.state.status
    this.setStatus('checking')

      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.recvi.ru'
    
    // Try primary health endpoint first
    const isHealthy = await this.tryEndpoint(`${apiBaseUrl}${this.API_HEALTH_ENDPOINT}`)
    
    if (isHealthy) {
      if (prevStatus !== 'online') {
        console.log('[NetworkManager] ✅ Connection restored via health endpoint')
        await LogsRepo.save('info', 'Connection restored')
      }
      this.setStatus('online')
      return
    }
    
    // Fallback: try API base endpoint
    const isFallbackHealthy = await this.tryEndpoint(`${apiBaseUrl}${this.API_FALLBACK_ENDPOINT}`)
    
    if (isFallbackHealthy) {
      if (prevStatus !== 'online') {
        console.log('[NetworkManager] ✅ Connection restored via fallback endpoint')
        await LogsRepo.save('info', 'Connection restored (fallback)')
      }
      this.setStatus('online')
      return
    }
    
    // Both failed - we're offline
    if (prevStatus === 'online') {
      console.warn('[NetworkManager] ❌ Connection lost (both endpoints failed)')
      await LogsRepo.save('warn', 'Connection lost')
    }
    this.setStatus('offline')
  }
  
  /**
   * Пробует подключиться к эндпоинту
   */
  private static async tryEndpoint(url: string): Promise<boolean> {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), this.PING_TIMEOUT)

      const response = await fetch(url, {
        method: 'HEAD',
        signal: controller.signal,
        cache: 'no-store',
      })

      clearTimeout(timeoutId)

      // Any response (even 4xx) means the server is reachable
      // We only care about network connectivity, not authorization
      return response.status < 500
    } catch (error) {
      return false
    }
  }

  /**
   * Устанавливает статус и уведомляет слушателей
   */
  private static setStatus(status: NetworkStatus): void {
    const prevStatus = this.state.status
    
    if (status === prevStatus) {
      return
    }

    this.state.status = status

    // Обновляем временные метки
    if (status === 'online') {
      this.state.lastOnlineAt = new Date()
      // При восстановлении сети переключаемся на больший интервал
      this.restartPeriodicCheck()
    } else if (status === 'offline') {
      this.state.lastOfflineAt = new Date()
      // При потере сети переключаемся на меньший интервал
      this.restartPeriodicCheck()
    }

    console.log(`[NetworkManager] Status changed: ${prevStatus} → ${status}`)

    // Уведомляем всех слушателей
    this.state.listeners.forEach((listener) => {
      try {
        listener(status)
      } catch (error) {
        console.error('[NetworkManager] Listener error:', error)
      }
    })
  }

  /**
   * Запускает периодическую проверку подключения
   */
  private static startPeriodicCheck(): void {
    if (this.state.checkInterval !== null) {
      return // Уже запущена
    }

    const interval = this.state.status === 'online' 
      ? this.PING_INTERVAL_ONLINE 
      : this.PING_INTERVAL_OFFLINE

    console.log(`[NetworkManager] Starting periodic check every ${interval / 1000}s`)

    this.state.checkInterval = window.setInterval(() => {
      this.checkConnection()
    }, interval)
  }

  /**
   * Останавливает периодическую проверку
   */
  private static stopPeriodicCheck(): void {
    if (this.state.checkInterval !== null) {
      clearInterval(this.state.checkInterval)
      this.state.checkInterval = null
      console.log('[NetworkManager] Stopped periodic check')
    }
  }

  /**
   * Перезапускает периодическую проверку с новым интервалом
   */
  private static restartPeriodicCheck(): void {
    this.stopPeriodicCheck()
    this.startPeriodicCheck()
  }

  /**
   * Форсирует немедленную проверку подключения
   */
  static forceCheck(): void {
    console.log('[NetworkManager] Force checking connection...')
    this.checkConnection()
  }
}

